import { createSimpleMutator } from '@superbet-group/web.lib.vue-utilities';
import { keyBy as _keyBy } from 'lodash';
import {
    ActionContext,
    Commit,
    GetterTree,
} from 'vuex';

import { logger } from '@app/logger';
import { IState } from '@app/store';
import OfferEvent from '@models/offer/OfferEvent';
import {
    StatsCompetition,
    VirtualTournament,
} from '@sportOfferFederation/competitions/types';
import { getEventByTournamentFilter } from '@sportOfferFederation/competitions/virtual-tournaments/filterUtilities';
import { ComputedGetters } from '@store/StoreUtil';
import { TournamentId } from '@store/modules/ui/sportOffer/marketFilters/types';

import {
    getCurrentVirtualTournament,
    getTournamentToVirtualTournamentMapping,
    mergeIntoVirtualTournaments,
    parseHotTournamentLink,
    virtualTournamentMergeFunction,
} from './helpers';
import { CompetitionsState } from './types';
import {
    HotTournament,
    HotTournamentLocation,
} from '@store/modules/data/sportOffer/types';
import { getHotTournaments } from '@core/services/offer/CmsOfferService';
import { errorGuard } from '@core/utils/ErrorGuard';
import { RelativeRootGetters } from '@store/types';
import {
    statsService,
    transformCompetitionToVirtualTournament,
} from '../../services/StatsService';
import { SportId } from '@core/constants';
import { getShellConfig } from '@src/config/shell/config';

function defaultState(): CompetitionsState {
    return {
        virtualTournaments: [],
        currentCompetitionAsVirtualTournament: null,
        isLoadingCompetition: true,
    };
}

const mutations = {
    SET_VIRTUAL_TOURNAMENTS: 'SET_VIRTUAL_TOURNAMENTS',
    SET_FEATURED_COMPETITIONS: 'SET_FEATURED_COMPETITIONS',
    SET_ARE_FEATURED_COMPETITIONS_READY: 'SET_ARE_FEATURED_COMPETITIONS_READY',
    SET_CURRENT_COMPETITION: 'SET_CURRENT_COMPETITION',
    SET_IS_LOADING_COMPETITION: 'SET_IS_LOADING_COMPETITION',
} as const;

type GetterWithAllArguments<T> = (
    state: CompetitionsState,
    getters: any,
    rootState: IState,
    rootGetters: any,
) => T;

interface StoreGetters {
    availableTournamentIds: GetterWithAllArguments<number[]>;
    currentVirtualTournament: GetterWithAllArguments<VirtualTournament | undefined>;
    tournamentToVirtualTournament(state: CompetitionsState, getters: any): Record<TournamentId, VirtualTournament>;
    homeHotTournaments(state: CompetitionsState): VirtualTournament[],
    virtualTournamentsMap(state: CompetitionsState): Record<string, VirtualTournament>;
    outrightOffer: GetterWithAllArguments<OfferEvent | null>;
    isLoadingCompetition: GetterWithAllArguments<boolean>;
}

type Getters = GetterTree<CompetitionsState, IState> & StoreGetters;

export type CompetitionDataGetters = ComputedGetters<Getters>;

const getters: Getters = {
    isLoadingCompetition(state) {
        return state.isLoadingCompetition;
    },
    availableTournamentIds(state, getters, rootState, rootGetters) {
        return rootGetters['ui/sportOffer/sports/competitionEvents']
            .map((event: OfferEvent) => event.tournamentId);
    },
    // TODO: All of the actual virtual tournament stuff can go after we
    // migrate fully to the new navigation
    currentVirtualTournament(state, getters, rootState, rootGetters) {
        if (state.currentCompetitionAsVirtualTournament) {
            return state.currentCompetitionAsVirtualTournament;
        } else {
            return getCurrentVirtualTournament(state.virtualTournaments, rootGetters);
        }
    },
    tournamentToVirtualTournament(state) {
        return getTournamentToVirtualTournamentMapping(state.virtualTournaments);
    },
    virtualTournamentsMap(state) {
        return _keyBy(state.virtualTournaments, 'id');
    },
    virtualTournamentsByTournamentId(state) {
        return _keyBy(state.virtualTournaments, 'tournamentId');
    },
    homeHotTournaments(state) {
        return state.virtualTournaments.filter((virtualTournament) => {
            return virtualTournament.isHot && virtualTournament.locations?.includes(HotTournamentLocation.Home);
        });
    },
    getHotTournamentsPrematchBySportId(state) {
        return (sportId: number) => {
            return state.virtualTournaments.filter((virtualTournament) => {
                return virtualTournament.isHot
                    && virtualTournament.locations?.includes(HotTournamentLocation.Prematch)
                    && virtualTournament.sportId === sportId;
            });
        };
    },
    outrightOffer(state, getters, rootState, rootGetters: RelativeRootGetters): OfferEvent | null {
        if (!getters.isOnCompetitionPage) {
            return null;
        }
        const currentTournamentId = rootGetters['ui/competitions/currentTournamentId'];
        const currentVirtualTournament = rootGetters['data/competitions/currentVirtualTournament'];
        const viableOutrightOffers = rootGetters['data/sportOffer/outrights'].filter(
            getEventByTournamentFilter(currentTournamentId, currentVirtualTournament),
        ).filter((outright) => {
            // When dealing with competitions, we might have stale outrights for tournaments that are part of the competition,
            // but not the current offer. This is a quick hack around that.
            // We can't just use the current tournament context, because we're _not_ in tournament context anymore.
            // Updated filtering business logic: [SB-35](https://axilis.atlassian.net/browse/SB-35)
            return outright.isViableForBetting();
        });

        return viableOutrightOffers
            .reduce<OfferEvent | null>((latest, outright) => {
                // In case of mulitple viable outrights, we should return the latest created one: [SB-35](https://axilis.atlassian.net/browse/SB-35)
                if (!latest || outright.offerId > latest.offerId) {
                    return outright;
                }

                return latest;
            }, null);
    },
    // TODO: Mixing UI and data, we should refactor these stores and
    // probably introduce clean code through composeables
    isOnCompetitionPage(_state, _getters, _rootState, rootGetters) {
        return rootGetters['ui/competitions/isOnCompetitionPage'];
    },
};

const store = {
    namespaced: true,

    state: defaultState(),

    getters,

    mutations: {
        [mutations.SET_VIRTUAL_TOURNAMENTS]: createSimpleMutator<CompetitionsState, 'virtualTournaments'>('virtualTournaments'),
        [mutations.SET_CURRENT_COMPETITION]: createSimpleMutator<CompetitionsState, 'currentCompetitionAsVirtualTournament'>('currentCompetitionAsVirtualTournament'),
        [mutations.SET_IS_LOADING_COMPETITION]: createSimpleMutator<CompetitionsState, 'isLoadingCompetition'>('isLoadingCompetition'),
    },

    actions: {
        async fetchHotTournaments(context: ActionContext<CompetitionsState, IState>): Promise<void> {
            const hotTournaments = await getHotTournaments();
            context.dispatch('mergeHotTournamentsToVirtualTournaments', { hotTournaments });
        },
        async fetchCompetitionById(
            context: ActionContext<CompetitionsState, IState>,
            payload: {
                competitionId: number;
                sportId: number;
            },
        ): Promise<void> {
            const {
                competitionId,
                sportId,
            } = payload;
            context.commit(mutations.SET_IS_LOADING_COMPETITION, true);
            context.commit(mutations.SET_CURRENT_COMPETITION, null);

            try {
                let statsCompetition;
                if (sportId === SportId.Football) {
                    statsCompetition = await errorGuard(() => statsService.getSoccerCompetitionById(competitionId));
                } else {
                    statsCompetition = await errorGuard(() => statsService.getCompetitionById(competitionId));
                }

                if (statsCompetition) {
                    const competition = transformCompetitionToVirtualTournament(
                        statsCompetition,
                        {
                            sportId,
                        },
                    );

                    context.commit(mutations.SET_CURRENT_COMPETITION, competition ?? null);
                }
            } catch (error) {
                logger.error(error as Error);
            } finally {
                context.commit(mutations.SET_IS_LOADING_COMPETITION, false);
            }
        },
        async fetchCompetitionDetailsByTournamentId(context: ActionContext<CompetitionsState, IState>, tournamentId?: number): Promise<void> {
            context.commit(mutations.SET_IS_LOADING_COMPETITION, true);

            const currentTournamentId = tournamentId ?? context.rootGetters['ui/competitions/currentTournamentId'];
            const currentSportId = context.rootGetters['ui/sportOffer/sports/currentSportId'];

            try {
                await resolveEventByTournament(context, currentTournamentId);
                if (currentTournamentId != null) {
                    const getSportIds = (await import('stats/getSportIds')).default;

                    const currentVirtualTournament = context.getters.currentVirtualTournament as VirtualTournament | undefined;
                    // funny, we call them differently
                    const isOfferSoccer = currentVirtualTournament?.sportId === SportId.Football;

                    let competitionDetails : StatsCompetition | null | undefined;
                    // if soccer, skip the call if we already have the data we need
                    if (!isOfferSoccer || !currentVirtualTournament?.competitionId) {
                        competitionDetails = await errorGuard(() => statsService.getCompetitionByTournamentBookieId(currentTournamentId));
                    }
                    // category uses stats sportId not offer sportId
                    const isStatsSoccer = isOfferSoccer || competitionDetails?.categorySportId === getSportIds().OfferSportIdsMappedToStatsSportIds[SportId.Football];

                    // we use a different endpoint for correct season feature list
                    if (isStatsSoccer) {
                        const resolvedCompetitionId = competitionDetails?.id ?? currentVirtualTournament?.competitionId;
                        competitionDetails = await errorGuard(() => statsService.getSoccerCompetitionById(resolvedCompetitionId!));
                    }

                    if (competitionDetails) {
                        const competition = transformCompetitionToVirtualTournament(
                            competitionDetails,
                            {
                                sportId: currentSportId,
                                categoryId: currentVirtualTournament?.categoryId,
                            },
                        );

                        context.commit(mutations.SET_CURRENT_COMPETITION, competition ?? null);
                    }
                }
            } catch (error) {
                context.commit(mutations.SET_CURRENT_COMPETITION, null);
                logger.error(error as Error);
            }

            context.commit(mutations.SET_IS_LOADING_COMPETITION, false);
        },
        async fetchCompetitionDetailsFromOfferMappings(
            context: ActionContext<CompetitionsState, IState>,
            { sportSlug, categorySlug, tournamentSlug }: { sportSlug?: string, categorySlug?: string, tournamentSlug?: string },
        ): Promise<void> {
            const rootDomain = window.location.hostname.replace(/\b(local.|stage.|on.|qa.|dev.|silent.|prod.)\b/gi, '');
            const tournamentsMap = await errorGuard(() => fetch(`https://${rootDomain}/static/offerMappings/sportTournamentMap_${getShellConfig().localConfig.urlLocale}.json`).then((response) => response.json()));
            if (!tournamentsMap) {
                return;
            }
            const tournamentId = tournamentsMap[`${sportSlug}---${categorySlug}---${tournamentSlug}`];
            if (tournamentId) {
                await context.dispatch('fetchCompetitionDetailsByTournamentId', tournamentId);
            }
        },
        clearCurrentCompetition(context: ActionContext<CompetitionsState, IState>): void {
            context.commit(mutations.SET_IS_LOADING_COMPETITION, true);
            context.commit(mutations.SET_CURRENT_COMPETITION, null);
        },
        clearFeaturedCompetitions(context: ActionContext<CompetitionsState, IState>) {
            context.commit(mutations.SET_VIRTUAL_TOURNAMENTS, []);
            context.commit(mutations.SET_FEATURED_COMPETITIONS, []);
            context.commit(mutations.SET_ARE_FEATURED_COMPETITIONS_READY, false);
        },
        mergeHotTournamentsToVirtualTournaments(
            { commit, state }: { commit : Commit, state: CompetitionsState },
            { hotTournaments }: { hotTournaments: HotTournament[] },
        ) {
            const virtualTournamentsFromHotTournaments: VirtualTournament[] = hotTournaments.map((hotTournament) => {
                const {
                    competitionId,
                    competitionTab,
                    marketGroupId,
                } = parseHotTournamentLink(hotTournament.link);

                return {
                    id: `${competitionId}`,
                    competitionId,
                    tournamentId: parseInt(`990${competitionId}`, 10),
                    sportId: hotTournament.sportId,
                    flagId: hotTournament.flagId,
                    categoryId: hotTournament.categoryId,
                    name: hotTournament.title,
                    childrenIds: hotTournament.tournamentIds,
                    isHot: true,
                    sportTag: hotTournament.sportTag,
                    colors: hotTournament.colors,
                    locations: hotTournament.locations,
                    competitionTab,
                    marketGroupId,
                };
            });
            const newVirtualTournaments = mergeIntoVirtualTournaments(
                state.virtualTournaments,
                virtualTournamentsFromHotTournaments,
                virtualTournamentMergeFunction,
            );
            commit(mutations.SET_VIRTUAL_TOURNAMENTS, newVirtualTournaments);
        },
    },
};

async function resolveEventByTournament(context: ActionContext<CompetitionsState, IState>, tournamentId: number): Promise<OfferEvent | undefined> {
    const initialEvent = context.rootGetters['data/sportOffer/events']
        .find((event: OfferEvent) => event.tournamentId === tournamentId);

    if (initialEvent != null) {
        return initialEvent;
    }

    await errorGuard(() => context.dispatch('data/sportOffer/fetchEventsForTournaments', [tournamentId], { root: true }));

    return context.rootGetters['data/sportOffer/events']
        .find((event: OfferEvent) => event.tournamentId === tournamentId);
}

export type CompetitionDataStore = typeof store;

export default store;
